import { useCallback } from 'react';

import type { Draft } from '@luna/state';
import { createSharedSlice } from '@luna/state';
import type { SignInfo as DefaultSignInfo } from '@shared/core-types';

interface Signing<SignInfo, SignedData> {
    signInfo: SignInfo | undefined;

    resolve(signedData?: SignedData): void;

    reject(reason: string): void;
}

const SIGN_INFO_DEFAULT_ID = 'NO_ID_DEFINED';
export const SIGNING_DEFAULT_REJECT_REASON = 'SIGNING_DEFAULT_REJECT_REASON';

type DeferredSigningOptions<SignInfo> = {
    id?: string;
    signInfo: SignInfo | undefined;
};
type UseDeferredSingingReturn<SignInfo, SignedData> = {
    signInfo: SignInfo | undefined;
    resolveSigning: (signedData?: SignedData) => void;
    rejectSigning: (reason?: any) => void;
    resetSigning: () => void;
};
type CreateSigningInfoReturn<SignInfo, SignedData> = {
    useDeferredSigning: (id?: string) => UseDeferredSingingReturn<SignInfo, SignedData>;
    deferredSigning: ({
        signInfo,
        id,
    }: DeferredSigningOptions<SignInfo>) => Promise<SignedData | undefined>;
};

export function createDeferredSigning<
    SignInfo = DefaultSignInfo,
    SignedData = any,
>(): CreateSigningInfoReturn<SignInfo, SignedData> {
    const useSigningInfoState = createSharedSlice({
        initialState: { signing: {} as Record<string, Signing<SignInfo, SignedData> | undefined> },
        reducers: {
            setSignInfo: (
                state,
                action: { payload: { id: string; signing: Signing<SignInfo, SignedData> } },
            ) => {
                state.signing[action.payload.id] = action.payload.signing as Draft<
                    Signing<SignInfo, SignedData>
                >;
            },
            resolveSignInfo: (state, action: { payload: { id: string } }) => {
                delete state.signing[action.payload.id];
            },
            rejectSignInfo: (state, action: { payload: { id: string } }) => {
                delete state.signing[action.payload.id];
            },
            // Should only be invoked when the signing is still pending but we want to restart for that given id.
            resetSignInfo: (state, action: { payload: { id: string } }) => {
                delete state.signing[action.payload.id];
            },
        },
    });

    const deferredSigning = ({
        id = SIGN_INFO_DEFAULT_ID,
        signInfo,
    }: DeferredSigningOptions<SignInfo>): Promise<SignedData | undefined> =>
        new Promise((resolve, reject) => {
            useSigningInfoState.dispatchActions.setSignInfo({
                id,
                signing: { signInfo, resolve, reject },
            });
        });

    const useDeferredSigning = (
        id = SIGN_INFO_DEFAULT_ID,
    ): UseDeferredSingingReturn<SignInfo, SignedData> => {
        const { signing, dispatchActions } = useSigningInfoState();

        const handleResolveSigning = useCallback(
            (signedData?: SignedData) => {
                const selectedSigning = signing[id];
                if (!selectedSigning) {
                    return;
                }

                selectedSigning.resolve(signedData);
                dispatchActions.resolveSignInfo({ id });
            },
            [signing, id, dispatchActions],
        );

        const handleRejectSigning = useCallback(
            (reason?: any) => {
                const selectedSigning = signing[id];
                if (!selectedSigning) {
                    return;
                }

                selectedSigning.reject(reason ?? SIGNING_DEFAULT_REJECT_REASON);
                dispatchActions.rejectSignInfo({ id });
            },
            [signing, id, dispatchActions],
        );

        const handleResetSigning = useCallback(() => {
            dispatchActions.resetSignInfo({ id });
        }, [id, dispatchActions]);

        return {
            signInfo: signing[id]?.signInfo,
            resolveSigning: handleResolveSigning,
            rejectSigning: handleRejectSigning,
            resetSigning: handleResetSigning,
        };
    };

    return {
        useDeferredSigning,
        deferredSigning,
    };
}
